/**
 * Vacancy and the owner management fee — page-side mirror.
 *
 * A vacant object is not billed. Neel, 2026-10-04: "they only get paid when we
 * collect rent, so when its not vacant".
 *
 * This deliberately duplicates `supabase/functions/_shared/owner-vacancy.ts`
 * rather than importing it: the edge functions are Deno modules and are not
 * typechecked by CI, so the two cannot share a build. They must still produce
 * the same number — the figure the owner accepts here is printed on the
 * Auftragsbestätigung generated there — and `owner-vacancy.parity.test.ts`
 * fails if they ever disagree.
 *
 * Keep the two files in step.
 */

/**
 * `properties.tenancy_status` is free text, so this matches the vacant
 * spellings the admin surfaces write. Anything else — including null or an
 * unanticipated spelling — is BILLABLE: a missing status must never silently
 * zero a real invoice.
 */
export const VACANT_STATUSES: readonly string[] = [
  "vacant",
  "leerstand",
  "leer",
  "unrented",
];

/**
 * Net management fee per room per month, applied when an invite has no
 * admin-configured fee, rather than silently billing 0. Neel, 2026-10-16:
 * raised from 33.
 *
 * Exported from the shared money module rather than repeated as a literal at
 * each call site: it is read by both documents the owner is bound by, and a
 * default that drifted between them is exactly the mismatch this module
 * exists to prevent. `owner-vacancy.parity.test.ts` holds the two copies to
 * the same value.
 */
export const DEFAULT_MANAGEMENT_FEE = 49;

/**
 * The NET per-room fee an invite's documents quote. Only a MISSING fee falls
 * back to the default: an explicit 0 is a deliberate zero-fee invite and must
 * stay 0, as the order screen already does (`??`). A truthiness check here
 * printed the default on the stored document for a zero-fee invite.
 */
export function resolveFeeNet(
  prefilled: number | string | null | undefined
): number {
  return prefilled == null ? DEFAULT_MANAGEMENT_FEE : Number(prefilled);
}

export function isVacantStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const normalised = status.trim().toLowerCase();
  if (!normalised) return false;
  return VACANT_STATUSES.includes(normalised);
}

/**
 * Monthly fee for one object, in whatever basis `feePerRoom` is given in.
 * Vacancy bills nothing; a missing fee or non-positive room count yields 0.
 */
export function ownerMonthlyFee(
  feePerRoom: number | null | undefined,
  rooms: number | null | undefined,
  tenancyStatus: string | null | undefined
): number {
  if (isVacantStatus(tenancyStatus)) return 0;
  if (!feePerRoom || feePerRoom <= 0) return 0;
  const billableRooms = rooms && rooms > 0 ? rooms : 1;
  return feePerRoom * billableRooms;
}

/** The onboarding management fee is always charged at the German standard rate. */
export const OWNER_FEE_VAT_RATE = 0.19;

export interface OwnerFeeBreakdown {
  /** Net fee per room per month, as configured. */
  ratePerRoomNet: number;
  /** Rooms actually billed (never below 1 for a billable object). */
  rooms: number;
  totalNet: number;
  vat: number;
  totalGross: number;
  vacant: boolean;
}

/**
 * Every figure the Auftragsbestätigung prints, from one place.
 *
 * `profiles.management_fee` -> `owner_onboarding_tokens.prefilled_fee_amount`
 * is NET per room per month. The order step says "netto" and adds VAT, and the
 * real management invoice prints "zzgl. MwSt. 19 %" — the fee is the base, VAT
 * goes on top. Both Auftragsbestätigung generators now read their numbers from
 * here so they cannot disagree about that again.
 */
export function ownerFeeBreakdown(
  feePerRoomNet: number | null | undefined,
  rooms: number | null | undefined,
  tenancyStatus: string | null | undefined
): OwnerFeeBreakdown {
  const vacant = isVacantStatus(tenancyStatus);
  const rate = feePerRoomNet && feePerRoomNet > 0 ? feePerRoomNet : 0;
  const billableRooms = rooms && rooms > 0 ? rooms : 1;
  const totalNet = roundCents(
    ownerMonthlyFee(feePerRoomNet, rooms, tenancyStatus)
  );
  const vat = roundCents(totalNet * OWNER_FEE_VAT_RATE);
  return {
    ratePerRoomNet: rate,
    rooms: billableRooms,
    totalNet,
    vat,
    // Summed from the rounded parts so the document always foots: a separately
    // rounded gross can differ from net + VAT by a cent.
    totalGross: roundCents(totalNet + vat),
    vacant,
  };
}

/**
 * Round to whole cents.
 *
 * The page formats with toLocaleString (half-up) and the PDF generators with
 * toFixed (binary), which disagree on an exact half-cent — 82.705 renders as
 * "82,71" on screen and "82.70" in the document. Rounding once, here, means
 * every surface formats an already-exact value.
 */
function roundCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
